import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom, mergeMap } from 'rxjs/operators';

import { FacebookActions, LocationsActions } from '@actions/index';
import * as fromApp from '@reducers/index';
import { FacebookServices }  from '@services/facebook/facebook.service';

import { HttpApiService } from '@services/http-api.service';
import { FacebookAuthResult, LocationModel, RequestModel, ReviewModel } from '@models/index';
import { RouterFacadeService } from '@services/router-facade.service';
import Swal from 'sweetalert2';
import { facebookErrorMapper } from '@utilities/function-utils';
import { FacebookErrorModel } from '@models/facebookError.model';

@Injectable()
export class FacebookEffects {
  constructor(
    private actions$: Actions,
    private store: Store<fromApp.State>,
    private facebookService: FacebookServices,
    private routerFacadeService: RouterFacadeService,
    private httpApiService: HttpApiService
  ) { }

  resetOnLocationChange$ = createEffect(() => this.actions$.pipe(
    ofType(LocationsActions.setCurrentLocation),
    map(_ => FacebookActions.reset())
  ))

  // TODO: fix names of the following 2 Effects to match their Actions (these were recently split)
  // used for Social Sharing page listing && Share Review
  findFacebookPageByLocationId$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.searchFacebookPagesByLocation),
    switchMap(({ locationId, TypeService:string }) => this.facebookService.findFacebookPages$({ locationId, TypeService:string }).pipe(
      mergeMap(selectedTokens => [
        FacebookActions.setSelectedTokens({ selectedTokens }),
        FacebookActions.searchFacebookPagesByLocationSuccess({ pages: selectedTokens })
      ]),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  // used ONLY for Auth process, and (currenty) only dispatched from this file / another effect.
  searchFacebookPagesByLocations$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.searchPagesByLocationId),
    switchMap(({ locationId, authResult }) => this.facebookService.searchFacebookPages$({ locationId, authResult }).pipe(
      mergeMap(pages => [
        FacebookActions.searchFacebookPagesByLocationSuccess({ pages }),
        FacebookActions.openSelectPagesModal({ isOpen: true })
      ]),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  enableFacebookPage$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.enableFacebookPage),
    switchMap(payload => this.facebookService.enableFacebookPage$(payload).pipe(
      map(result => FacebookActions.enableFacebookPageSuccess(result)),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  disableFacebookPage = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.disableFacebookPage),
    switchMap(payload => this.facebookService.disableFacebookPage$(payload).pipe(
      map(result => FacebookActions.disableFacebookPageSuccess(result)),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  removeFacebookPageSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.removeFacebookPage),
    switchMap((payload) => this.facebookService.removeFacebookPage$(payload).pipe(
      map(_ => FacebookActions.removeFacebookPageSuccess(payload)),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  searchScheduleSetting$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.searchScheduleSettings),
    switchMap(({ locationId }) => this.facebookService.getScheduleSettings$({ locationId }).pipe(
      map(settings => FacebookActions.searchScheduleSettingsSuccess({ settings })),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ));

  updateScheduleSettings$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.updateScheduleSettings),
    switchMap(({ settings }) =>
      this.store.select(fromApp.getSocialSharingScheduleSettings).pipe(
        take(1),
        map(prevValue => ({
          id: prevValue && prevValue.locationId == settings.locationId ? prevValue.id : null,
          ...settings
        })),
        switchMap(settings => this.facebookService.updateScheduleSettings$({ settings })),
        map(settings => FacebookActions.updateScheduleSettingsSuccess({ settings })),
        catchError(error => of(FacebookActions.error(error))),
      )
    ),
  ))

  searchTemplateSettings$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.searchTemplateSettings),
    switchMap(({ locationId }) => this.facebookService.getTemplateSettings$({ locationId }).pipe(
      map(settings => FacebookActions.searchTemplateSettingsSuccess({ settings })),
      catchError(error => of(FacebookActions.error(error))),
    )),
  ))

  updateTemplateSettings$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.updateTemplateSettings),
    switchMap(({ settings }) =>
      this.store.select(fromApp.getSocialSharingTemplateSettings).pipe(
        take(1),
        map(prevValue => ({
          id: prevValue && prevValue.locationId == settings.locationId ? prevValue.id : null,
          ...settings,
        })),
        switchMap(settings => this.facebookService.updateTemplateSettings$({ settings })),
        map(settings => FacebookActions.updateTemplateSettingsSuccess({ settings })),
        catchError(error => of(FacebookActions.error(error))),
      )
    )
  ))

  // 1. user initiated a facebook login
  authenticateFacebook$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.authenticateFacebook),
    map(FacebookActions.authenticateFacebook),
    withLatestFrom(
      this.store.select(fromApp.getLoggedUser),
      (action,user) => ({
        locationId: action.locationId,
        user: user,
        typeService: action.typeService
      })
    ),
    switchMap((result) => this.facebookService.authenticateWithFacebook(result.typeService,result.user.id).pipe(
      mergeMap(authResult => [
        // caches the auth token in storage for when we later call sendSelectedToken
        // TODO: is this necessary? FacebookTokenDTO are saved by searchpagesByLocationId's backedAPI
        FacebookActions.saveAuthToken({ authResult }),
        // TODO: change name to searchPagesByAuthResult
        // finds pages for the authResult -- locationId is actually only used by server to populate
        // the results' properties -- the locationId is not used for querying nor stored in the
        // database in the backing API call...
        FacebookActions.searchPagesByLocationId({locationId: result.locationId, authResult})
      ]),
      catchError(error => {
        console.log('[facebook.effects authenticateFacebook$] error:', error);
        console.log('[facebook.effects authenticateFacebook$] error:', JSON.stringify(error));

        return of(FacebookActions.error(error))
      }),
    )),
  ))
  // 2. after user is authed, front-end asks for user to choose a token via modal, leading us here
  sendSelectedToken$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.sendSelectedToken),
    map(action => action.payload),
    withLatestFrom(
      this.store.select(fromApp.getFacebookAuthResult),
      (action, authResult: FacebookAuthResult) => ({
        authResult, locationId: action.locationId, selectedPageId: action.selectedTokens[0].token.id
      })
    ),
    map(payload => FacebookActions.setupPagesFromAuthResultForLocation(
      payload
    )),
  ))

  // 3. triggered directly from the action above this one...
  // TODO: rename + combine 2 & 3 / sendSelectedToken & setupPagesFromAuthResultForLocation
  setupPagesFromAuthResultForLocation$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.setupPagesFromAuthResultForLocation),
    switchMap(({ authResult, locationId, selectedPageId }) =>
      this.facebookService.setPageToken$({
        locationId,
        authResult,
        selectedPageId
      }).pipe(
        mergeMap(selectedTokens => [
          FacebookActions.setSelectedTokens({
            // authResult,
            selectedTokens
          }),
          FacebookActions.searchFacebookPagesByLocationSuccess({ pages: selectedTokens })
        ]),
        catchError(error => {
          console.log('[facebook.service setupPagesFromAuthResultForLocation$] error:', error);
          console.log('[facebook.service setupPagesFromAuthResultForLocation$] error:', JSON.stringify(error));

          return of(FacebookActions.error(error))
        }),
      )
    ),
  ))

  updateCommentOnFacebookPost$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.updateCommentOnFacebookPost),
    map(action => action.request),
    switchMap((request) => 
      this.httpApiService.postRequest(request)
      .pipe(
          tap(data => this.launchAlertMssg('Reply Posted', 'Your reply was posted via Facebook!','success')),
          map((data:ReviewModel) => data ),
          map(data => FacebookActions.updateCommentOnFacebookPostSuccess({review: data })),
          catchError(error => of(FacebookActions.setFacebookError({error: facebookErrorMapper(error as FacebookErrorModel)})))
      )
    )
  ));

  shareReview$ = createEffect(() => this.actions$.pipe(
    ofType(FacebookActions.shareReview),
    switchMap(({ locationFacebookTokenPair, reviewId }) => {
      return this.facebookService.shareReviewFromLocationToPage$({
        locationFacebookTokenPair, reviewId
      }).pipe(
        map(result => FacebookActions.shareReviewSuccess({})),
        catchError(error => of(FacebookActions.error(error)))
      )
    }),
  ));

  commentFacebookPost$ = createEffect(() =>
    this.actions$.pipe(
        ofType(FacebookActions.commentFacebookPost),
        map(action => action.request),
        withLatestFrom(
          this.store.select(fromApp.getCurrentLocation),
          (request: RequestModel, location: LocationModel) => ({
              ...request,
              params: request.params + `&locationId=${location.id}`
          })
        ),
        switchMap((request) =>
            this.httpApiService.postRequest(request)
                .pipe(
                    tap(() => this.launchAlertMssg('Reply Posted', 'Your reply was posted via Facebook!','success')),
                    map((data) => data as ReviewModel),
                    map(review => FacebookActions.commentFacebookPostSuccess({ review })),
                    catchError(error => of(FacebookActions.setFacebookError({error: facebookErrorMapper(error as FacebookErrorModel)})))
                )
         )
    )
  );

  launchAlertMssg(title: string, message: string, type: any) {
    Swal.fire({
      title: title,
      html: message,
      icon: type,
      showCancelButton: false,
      confirmButtonText: 'OK'
    }).then(result => {
        if(result.value && type == 'error') this.routerFacadeService.navigateTo$('admin-location/location-review-targets')
    })
  }

}
